import type { DdmStream, Extractor, SourceDevice, Stream } from '@bloo-team/api-client'
import { isCustom, isDraft, isEnabled, matchesFilter, needsAttention, updatedAtMs, type VolumeMap } from './extractors'
import { lastSeenMs, silentMuted, sourceState, streamsWithoutDdm, type SourceState } from './pipeline-health'
import { effectiveRetention, rowDrifts, type StreamConfigLevels } from './retention'
import { streamFacts } from './data-model'
import type { CheckLink, Read } from './fabric-overview'

/**
 * Datafabric · Overview — the plant model (docs/fabric-overview.md §The plant).
 *
 * The page draws the pipeline as a process plant: five stages left to right
 * (Collection · Extraction · Enrichment · Data model · Storage), pipes with
 * instruments between them, a losses band beneath. Everything here is pure:
 * the same reads and predicates the target pages use (`pipeline-health.ts`,
 * `extractors.ts`, `data-model.ts`, `retention.ts`), so a number on the plant
 * and the rows behind its link cannot disagree.
 *
 * Scale is the design constraint (thousands of devices, hundreds of
 * extractors): every list is a `Fold` — the full ranked list plus a display
 * limit. The plant shows the first `limit`, problems first; the Inspect drawer
 * shows the rest, filterable. Ranking is deterministic (priority, then age,
 * then name) so the plant does not reshuffle on refresh.
 */

export type Stage = 'collection' | 'extraction' | 'enrichment' | 'data-model' | 'storage'

export const STAGES: ReadonlyArray<{ key: Stage; title: string; reads: string }> = [
  { key: 'collection', title: 'Collection', reads: 'device-management · stream-management · daily-stream-details' },
  { key: 'extraction', title: 'Extraction', reads: 'parsers/list · 24h volume scan' },
  { key: 'enrichment', title: 'Enrichment', reads: 'no read wired' },
  { key: 'data-model', title: 'Data model', reads: 'ddm/list' },
  { key: 'storage', title: 'Storage', reads: 'stream-config · stream-management' },
]

/** ISA-101 priority: shape + colour, never colour alone. `info` never counts as abnormal. */
export type Priority = 'high' | 'medium' | 'info'

export type ReadState = 'ok' | 'loading' | 'error'

/** A ranked list with a display limit: the plant shows `items.slice(0, limit)`, Inspect shows all. */
export interface Fold<T> {
  items: T[]
  limit: number
}

export const fold = <T,>(items: T[], limit: number): Fold<T> => ({ items, limit })
export const shown = <T,>(f: Fold<T>): T[] => f.items.slice(0, f.limit)
export const hidden = <T,>(f: Fold<T>): number => Math.max(0, f.items.length - f.limit)

export const INLET_LIMIT = 8
export const OUTLET_LIMIT = 6
export const PENDING_LIMIT = 5
export const TYPE_LIMIT = 5
export const DISK_LIMIT = 3
export const ALARM_LIMIT = 10

/* ------------------------------------------------------------ alarms */

export interface Alarm {
  id: string
  /** The overview check this row belongs to (`fabric-overview.ts` ids). */
  checkId: string
  priority: Priority
  stage: Stage
  /** The entity: device address, stream name, extractor name. */
  subject: string
  /** What is wrong, in the target page's own words. */
  reason: string
  /** Age of the condition in ms when the read carries one (device last seen, extractor updated). */
  ageMs: number | null
  link: CheckLink
}

const PRIORITY_RANK: Record<Priority, number> = { high: 0, medium: 1, info: 2 }

/** Priority first, then oldest first (unknown age last), then by subject. */
export function byAlarmOrder(a: Alarm, b: Alarm): number {
  const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
  if (p !== 0) return p
  if (a.ageMs !== b.ageMs) {
    if (a.ageMs === null) return 1
    if (b.ageMs === null) return -1
    return b.ageMs - a.ageMs
  }
  return a.subject.localeCompare(b.subject)
}

/* -------------------------------------------------------- collection */

export interface Inlet {
  address: string
  state: SourceState
  lastSeenMs: number | null
  /** First extractor bound to the device, or null when none is configured. */
  extractor: string | null
  streams: string[]
}

export interface Outlet {
  name: string
  /** Null while the daily read is pending or failed. */
  eventsToday: number | null
  /** 0..1 of the busiest stream today. */
  share: number
  sources: number | null
  state: 'ok' | 'silent' | 'unmapped' | 'muted'
}

export interface CollectionModel {
  read: ReadState
  devicesLoaded: number
  /** Server-side total; null until the read lands. */
  devicesTotal: number | null
  /** Devices archived in this browser and dropped before classification. */
  archivedHidden: number
  healthy: number
  inactive: number
  stale: number
  decommissioned: number
  inlets: Fold<Inlet>
  outlets: Fold<Outlet>
  streamsTotal: number
  eventsToday: number | null
  /** Streams with at least one event today. */
  reporting: number | null
  /** Before 06:00 every stream reads 0, so silence is not news. */
  muted: boolean
}

const STATE_RANK: Record<SourceState, number> = { inactive: 0, stale: 1, decommissioned: 2, active: 3 }

/**
 * Problem devices first (inactive, stale, then likely decommissioned), each
 * group longest unseen first; healthy devices last, most recently seen first
 * so the manifold still shows live inlets when nothing is wrong.
 */
export function rankInlets(devices: SourceDevice[], now: number): Inlet[] {
  return devices
    .map((d): Inlet => ({
      address: d.device_address,
      state: sourceState(d, now),
      lastSeenMs: lastSeenMs(d),
      extractor: d.extractor[0] ?? null,
      streams: d.stream,
    }))
    .sort((a, b) => {
      const r = STATE_RANK[a.state] - STATE_RANK[b.state]
      if (r !== 0) return r
      const sa = a.lastSeenMs ?? 0
      const sb = b.lastSeenMs ?? 0
      if (sa !== sb) return a.state === 'active' ? sb - sa : sa - sb
      return a.address.localeCompare(b.address)
    })
}

const OUTLET_RANK: Record<Outlet['state'], number> = { unmapped: 0, silent: 1, muted: 2, ok: 3 }

/** Streams with a problem first, then busiest today first, then by name. */
export function rankOutlets(streams: Stream[], daily: Record<string, number> | undefined, sourceCounts: Record<string, number> | undefined, muted: boolean): Outlet[] {
  const max = daily ? Math.max(0, ...streams.map((s) => daily[s.stream] ?? 0)) : 0
  return streams
    .map((s): Outlet => {
      const events = daily ? (daily[s.stream] ?? 0) : null
      const sources = sourceCounts ? (sourceCounts[s.stream] ?? 0) : null
      const state: Outlet['state'] = sources === 0 ? 'unmapped' : events === 0 ? (muted ? 'muted' : 'silent') : 'ok'
      return { name: s.stream, eventsToday: events, share: max > 0 && events ? events / max : 0, sources, state }
    })
    .sort((a, b) => OUTLET_RANK[a.state] - OUTLET_RANK[b.state] || (b.eventsToday ?? 0) - (a.eventsToday ?? 0) || a.name.localeCompare(b.name))
}

/* -------------------------------------------------------- extraction */

export interface PendingItem {
  name: string
  extractorId: string
  reason: string
  priority: Priority
  updatedMs: number | null
  link: CheckLink
}

export interface TypeCount {
  type: string
  total: number
  enabled: number
}

export interface ExtractionModel {
  read: ReadState
  total: number
  enabled: number
  custom: number
  /** Extractors a human has to act on: validation not successful, or an unpublished draft. */
  pending: Fold<PendingItem>
  /** Routing summary: extractors grouped by `source_type`, busiest group first. */
  byType: Fold<TypeCount>
  scan: { state: 'idle' | 'running' | 'failed' | 'done'; silent: number | null; fallout: number | null }
}

const FAILED_STAGE = /fail/i
const ATTENTION_LINK: CheckLink = { to: '/fabric/extractors', search: { filter: 'attention' } }
const DRAFT_LINK: CheckLink = { to: '/fabric/extractors', search: { filter: 'draft' } }

/** A failed integration is high; every other non-success stage, and a draft, is medium. */
export function pendingFor(e: Extractor): PendingItem | null {
  const attention = needsAttention(e)
  const draft = isDraft(e)
  if (!attention && !draft) return null
  const stage = e.validation_stage
  const reasons: string[] = []
  if (draft) reasons.push('draft')
  if (attention) reasons.push(stage === null ? 'no validation result' : stage)
  return {
    name: e.name,
    extractorId: e.extractor_id,
    reason: reasons.join(' · '),
    priority: stage !== null && FAILED_STAGE.test(stage) ? 'high' : 'medium',
    updatedMs: updatedAtMs(e),
    link: attention ? ATTENTION_LINK : DRAFT_LINK,
  }
}

/** Highest priority first, then longest pending, then by name. */
export function rankPending(extractors: Extractor[]): PendingItem[] {
  return extractors
    .map(pendingFor)
    .filter((p): p is PendingItem => p !== null)
    .sort((a, b) => {
      const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
      if (p !== 0) return p
      const ua = a.updatedMs ?? Number.MAX_SAFE_INTEGER
      const ub = b.updatedMs ?? Number.MAX_SAFE_INTEGER
      return ua - ub || a.name.localeCompare(b.name)
    })
}

export function extractorsByType(extractors: Extractor[]): TypeCount[] {
  const map = new Map<string, TypeCount>()
  for (const e of extractors) {
    const type = e.source_type?.trim() || 'untyped'
    const row = map.get(type) ?? { type, total: 0, enabled: 0 }
    row.total += 1
    if (isEnabled(e)) row.enabled += 1
    map.set(type, row)
  }
  return [...map.values()].sort((a, b) => b.total - a.total || a.type.localeCompare(b.type))
}

/* -------------------------------------------------------- data model */

export interface DataModelModel {
  read: ReadState
  declared: number
  withData: number
  /** Declared streams with no data behind them (information, not a problem). */
  empty: string[]
  /** Streams holding data with no DDM row, largest first. */
  missing: string[]
}

/* ----------------------------------------------------------- storage */

export interface DiskRow {
  stream: string
  bytes: number
  share: number
}

export interface StorageModel {
  read: ReadState
  /** Effective cluster/scope retention in days, null when unknown. */
  retentionDays: number | null
  replication: number | null
  totalBytes: number
  onDisk: Fold<DiskRow>
  noRetention: string[]
  drift: string[]
  replicated: string[]
}

/** Largest on disk first; share is of the largest stream. */
export function rankOnDisk(streams: Stream[]): DiskRow[] {
  const max = Math.max(0, ...streams.map((s) => s.size_on_disk))
  return streams
    .map((s): DiskRow => ({ stream: s.stream, bytes: s.size_on_disk, share: max > 0 ? s.size_on_disk / max : 0 }))
    .sort((a, b) => b.bytes - a.bytes || a.stream.localeCompare(b.stream))
}

/* -------------------------------------------------------------- loss */

export interface Loss {
  id: string
  stage: Stage
  label: string
  count: number | null
  priority: Priority
  link: CheckLink
  /** `idle` = needs the 24h scan; `loading`/`error` = the read behind it. */
  state: 'ok' | 'issue' | 'idle' | 'loading' | 'error' | 'muted'
}

/* ------------------------------------------------------------- model */

export interface PlantModel {
  collection: CollectionModel
  extraction: ExtractionModel
  dataModel: DataModelModel
  storage: StorageModel
  losses: Loss[]
  /** Every abnormal row across the plant, ranked; `info` rows last. */
  alarms: Fold<Alarm>
  abnormal: number
  info: number
  /** Stages with at least one abnormal row. */
  abnormalStages: Stage[]
}

export interface PlantInput {
  now: number
  /** The clock device ages read — the device read's own `dataUpdatedAt`, as the Sources page uses. Defaults to `now`. */
  sourcesNow?: number
  sources: Read<{ devices: SourceDevice[]; total: number }>
  archived: ReadonlySet<string>
  streams: Read<{ streams: Stream[] }>
  daily: Read<Record<string, number>>
  sourceCounts: Read<Record<string, number>>
  extractors: Read<{ extractors: Extractor[] }>
  /** Null = the 24h scan has never run (idle). */
  volumes: Read<VolumeMap> | null
  ddm: Read<DdmStream[]>
  config: Read<unknown> & StreamConfigLevels
}

const readState = (...reads: Read<unknown>[]): ReadState =>
  reads.some((r) => r.isError) ? 'error' : reads.some((r) => r.isPending) ? 'loading' : 'ok'

const SOURCES_LINK = (status: SourceState): CheckLink => ({ to: '/fabric/sources', search: { status } })
const STREAMS_LINK = (filter: 'silent' | 'unmapped'): CheckLink => ({ to: '/fabric/streams', search: { filter } })

export function buildPlant(input: PlantInput): PlantModel {
  const { now, archived } = input
  const muted = silentMuted(now)
  const alarms: Alarm[] = []
  const losses: Loss[] = []

  /* collection */
  const page = input.sources.data
  const loaded = page?.devices ?? []
  const devices = archived.size === 0 ? loaded : loaded.filter((d) => !archived.has(d.device_address))
  const sourcesNow = input.sourcesNow ?? now
  const inlets = rankInlets(devices, sourcesNow)
  const count = (s: SourceState) => inlets.filter((i) => i.state === s).length
  for (const i of inlets) {
    const age = i.lastSeenMs === null ? null : sourcesNow - i.lastSeenMs
    if (i.state === 'inactive') alarms.push({ id: `dev:${i.address}`, checkId: 'sources-inactive', priority: 'high', stage: 'collection', subject: i.address, reason: i.extractor ? `inactive · ${i.extractor}` : 'inactive · no extractor', ageMs: age, link: SOURCES_LINK('inactive') })
    else if (i.state === 'stale') alarms.push({ id: `dev:${i.address}`, checkId: 'sources-stale', priority: 'medium', stage: 'collection', subject: i.address, reason: i.extractor ? `not seen in 24h · ${i.extractor}` : 'not seen in 24h', ageMs: age, link: SOURCES_LINK('stale') })
    else if (i.state === 'decommissioned') alarms.push({ id: `dev:${i.address}`, checkId: 'sources-decommissioned', priority: 'info', stage: 'collection', subject: i.address, reason: 'likely decommissioned (30d+)', ageMs: age, link: SOURCES_LINK('decommissioned') })
  }
  const streamList = input.streams.data?.streams ?? []
  const outlets = rankOutlets(streamList, input.daily.data, input.sourceCounts.data, muted)
  for (const o of outlets) {
    if (o.state === 'unmapped') alarms.push({ id: `stream:${o.name}`, checkId: 'streams-unmapped', priority: 'high', stage: 'collection', subject: o.name, reason: 'no sources', ageMs: null, link: STREAMS_LINK('unmapped') })
    else if (o.state === 'silent') alarms.push({ id: `stream:${o.name}`, checkId: 'streams-silent', priority: 'medium', stage: 'collection', subject: o.name, reason: 'silent today', ageMs: null, link: STREAMS_LINK('silent') })
  }
  const dailyOk = input.daily.data !== undefined && !input.daily.isError
  const collection: CollectionModel = {
    read: readState(input.sources, input.streams),
    devicesLoaded: loaded.length,
    devicesTotal: page ? page.total : null,
    archivedHidden: loaded.length - devices.length,
    healthy: count('active'),
    inactive: count('inactive'),
    stale: count('stale'),
    decommissioned: count('decommissioned'),
    inlets: fold(inlets, INLET_LIMIT),
    outlets: fold(outlets, OUTLET_LIMIT),
    streamsTotal: streamList.length,
    eventsToday: dailyOk ? streamList.reduce((a, s) => a + (input.daily.data![s.stream] ?? 0), 0) : null,
    reporting: dailyOk ? streamList.filter((s) => (input.daily.data![s.stream] ?? 0) > 0).length : null,
    muted,
  }
  const silentCount = outlets.filter((o) => o.state === 'silent' || o.state === 'muted').length
  losses.push(
    { id: 'streams-silent', stage: 'collection', label: muted ? 'no events yet today' : 'silent today', count: dailyOk ? silentCount : null, priority: 'medium', link: STREAMS_LINK('silent'), state: !dailyOk ? readState(input.streams, input.daily) === 'error' ? 'error' : 'loading' : muted ? 'muted' : silentCount ? 'issue' : 'ok' },
    { id: 'streams-unmapped', stage: 'collection', label: 'no sources', count: input.sourceCounts.data ? outlets.filter((o) => o.state === 'unmapped').length : null, priority: 'high', link: STREAMS_LINK('unmapped'), state: !input.sourceCounts.data ? readState(input.streams, input.sourceCounts) === 'error' ? 'error' : 'loading' : outlets.some((o) => o.state === 'unmapped') ? 'issue' : 'ok' },
  )

  /* extraction */
  const extractors = input.extractors.data?.extractors ?? []
  const pending = rankPending(extractors)
  for (const p of pending) {
    alarms.push({ id: `ext:${p.extractorId}`, checkId: p.link.search?.filter === 'draft' ? 'extractors-draft' : 'extractors-attention', priority: p.priority, stage: 'extraction', subject: p.name, reason: p.reason, ageMs: p.updatedMs === null ? null : now - p.updatedMs, link: p.link })
  }
  const vol = input.volumes
  const scanState: ExtractionModel['scan']['state'] = vol === null ? 'idle' : vol.isError ? 'failed' : vol.isPending || vol.data === undefined ? 'running' : 'done'
  let silent: number | null = null
  let fallout: number | null = null
  if (scanState === 'done' && vol?.data) {
    const map = vol.data
    // The Extractors page's own chip predicates, so the counts land on the same rows.
    const silentRows = extractors.filter((e) => matchesFilter(e, 'silent', map))
    const falloutRows = extractors.filter((e) => matchesFilter(e, 'fallout', map))
    silent = silentRows.length
    fallout = falloutRows.length
    for (const e of silentRows) alarms.push({ id: `ext-silent:${e.extractor_id}`, checkId: 'extractors-silent', priority: 'medium', stage: 'extraction', subject: e.name, reason: 'enabled · silent 24h', ageMs: null, link: { to: '/fabric/extractors', search: { filter: 'silent' } } })
    for (const e of falloutRows) alarms.push({ id: `ext-fallout:${e.extractor_id}`, checkId: 'extractors-fallout', priority: 'medium', stage: 'extraction', subject: e.name, reason: `falling to OTHER · ${map[e.extractor_id]?.fallout ?? 0}`, ageMs: null, link: { to: '/fabric/extractors', search: { filter: 'fallout' } } })
  }
  const extraction: ExtractionModel = {
    read: readState(input.extractors),
    total: extractors.length,
    enabled: extractors.filter(isEnabled).length,
    custom: extractors.filter(isCustom).length,
    pending: fold(pending, PENDING_LIMIT),
    byType: fold(extractorsByType(extractors), TYPE_LIMIT),
    scan: { state: scanState, silent, fallout },
  }
  const scanLossState = (n: number | null): Loss['state'] => scanState === 'idle' ? 'idle' : scanState === 'failed' ? 'error' : scanState === 'running' ? 'loading' : n ? 'issue' : 'ok'
  losses.push(
    { id: 'extractors-fallout', stage: 'extraction', label: 'falling to OTHER', count: fallout, priority: 'medium', link: { to: '/fabric/extractors', search: { filter: 'fallout' } }, state: scanLossState(fallout) },
    { id: 'extractors-silent', stage: 'extraction', label: 'enabled · silent 24h', count: silent, priority: 'medium', link: { to: '/fabric/extractors', search: { filter: 'silent' } }, state: scanLossState(silent) },
  )

  /* data model */
  const ddmRows = input.ddm.data ?? []
  const ddmRead = readState(input.ddm, input.streams)
  const facts = ddmRead === 'ok' ? streamFacts(ddmRows, streamList.map((s) => s.stream)) : null
  const empty = facts ? [...facts].filter(([, f]) => !f.hasData).map(([name]) => name).sort((a, b) => a.localeCompare(b)) : []
  const missing = ddmRead === 'ok' ? streamsWithoutDdm(streamList, ddmRows) : []
  for (const name of missing) alarms.push({ id: `ddm-missing:${name}`, checkId: 'data-model-missing', priority: 'high', stage: 'data-model', subject: name, reason: 'data with no data model', ageMs: null, link: { to: '/fabric/data-model', search: { chip: 'missing' } } })
  for (const name of empty) alarms.push({ id: `ddm-empty:${name}`, checkId: 'data-model-empty', priority: 'info', stage: 'data-model', subject: name, reason: 'modelled, no data', ageMs: null, link: { to: '/fabric/data-model', search: { chip: 'empty' } } })
  const dataModel: DataModelModel = {
    read: ddmRead,
    declared: ddmRows.length,
    withData: facts ? [...facts.values()].filter((f) => f.hasData).length : 0,
    empty,
    missing,
  }
  losses.push(
    { id: 'data-model-missing', stage: 'data-model', label: 'data, no model', count: ddmRead === 'ok' ? missing.length : null, priority: 'high', link: { to: '/fabric/data-model', search: { chip: 'missing' } }, state: ddmRead === 'ok' ? (missing.length ? 'issue' : 'ok') : ddmRead },
    { id: 'data-model-empty', stage: 'data-model', label: 'modelled, no data', count: ddmRead === 'ok' ? empty.length : null, priority: 'info', link: { to: '/fabric/data-model', search: { chip: 'empty' } }, state: ddmRead === 'ok' ? (empty.length ? 'issue' : 'ok') : ddmRead },
  )

  /* storage */
  const effective = effectiveRetention(input.config.cluster, input.config.scope)
  const streamsRead = readState(input.streams)
  const byName = (a: string, b: string) => a.localeCompare(b)
  const noRetention = streamsRead === 'ok' ? streamList.filter((s) => s.retention === null).map((s) => s.stream).sort(byName) : []
  const replicated = streamsRead === 'ok' ? streamList.filter((s) => (s.replication ?? 0) > 1).map((s) => s.stream).sort(byName) : []
  const driftRead = readState(input.streams, input.config)
  const drift = driftRead === 'ok' ? streamList.filter((s) => rowDrifts(s, effective)).map((s) => s.stream).sort(byName) : []
  for (const name of noRetention) alarms.push({ id: `ret-none:${name}`, checkId: 'storage-no-retention', priority: 'high', stage: 'storage', subject: name, reason: 'no retention', ageMs: null, link: { to: '/fabric/storage', search: { tab: 'capacity', chip: 'no-retention' } } })
  for (const name of drift) alarms.push({ id: `ret-drift:${name}`, checkId: 'storage-drift', priority: 'medium', stage: 'storage', subject: name, reason: 'retention differs from default', ageMs: null, link: { to: '/fabric/storage', search: { tab: 'retention' } } })
  for (const name of replicated) alarms.push({ id: `rep:${name}`, checkId: 'storage-replicated', priority: 'medium', stage: 'storage', subject: name, reason: 'replication > 1', ageMs: null, link: { to: '/fabric/storage', search: { tab: 'capacity', chip: 'replicated' } } })
  const storage: StorageModel = {
    read: streamsRead,
    retentionDays: effective?.retention ?? null,
    replication: effective?.replication ?? null,
    totalBytes: streamList.reduce((a, s) => a + s.size_on_disk, 0),
    onDisk: fold(rankOnDisk(streamList), DISK_LIMIT),
    noRetention,
    drift,
    replicated,
  }
  losses.push({ id: 'storage-no-retention', stage: 'storage', label: 'no retention', count: streamsRead === 'ok' ? noRetention.length : null, priority: 'high', link: { to: '/fabric/storage', search: { tab: 'capacity', chip: 'no-retention' } }, state: streamsRead === 'ok' ? (noRetention.length ? 'issue' : 'ok') : streamsRead })

  alarms.sort(byAlarmOrder)
  const abnormalRows = alarms.filter((a) => a.priority !== 'info')
  return {
    collection,
    extraction,
    dataModel,
    storage,
    losses,
    alarms: fold(alarms, ALARM_LIMIT),
    abnormal: abnormalRows.length,
    info: alarms.length - abnormalRows.length,
    abnormalStages: STAGES.map((s) => s.key).filter((k) => abnormalRows.some((a) => a.stage === k)),
  }
}
